import { z } from "zod";

/** Every API error uses this envelope (spec §7.1). */
export const ERROR_CODES = [
  "bad_request",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "rate_limited",
  "entitlement_required",
  "limit_reached",
  "region_unavailable",
  "programme_unsupported",
  "internal",
] as const;
export const errorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string().min(1),
    details: z.record(z.string(), z.unknown()).default({}),
  }),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

/** `details` for limit_reached — clients render the limit state from this, never guess (§7.1). */
export const limitReachedDetailsSchema = z.object({
  limit: z.string(),
  allowed: z.number().int().nonnegative(),
  used: z.number().int().nonnegative(),
  resets_at: z.string(),
});

/** `details` for entitlement_required — names the Pro feature the paywall should lead with. */
export const entitlementRequiredDetailsSchema = z.object({ feature: z.string() });

export const HTTP_STATUS: Record<ErrorCode, number> = {
  bad_request: 400,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  rate_limited: 429,
  entitlement_required: 402,
  limit_reached: 402,
  region_unavailable: 403,
  programme_unsupported: 422,
  internal: 500,
};

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }

  get status(): number {
    return HTTP_STATUS[this.code];
  }

  toEnvelope(): ErrorEnvelope {
    return { error: { code: this.code, message: this.message, details: this.details } };
  }
}
